import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription } from "rxjs";
import { COMMAND_NAME_VALID_REGEXP, COMMAND_NAME_VALID_REGEXP_NOT_DOT } from "src/app/conf/commands";
import { AutomationsAlias, InputsAlias, OutputsAlias, SelectorsAlias } from "src/app/model/automations-alias";
import { StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { AppState } from "src/app/store/app.reducer";
import * as DeviceActions from 'src/app/store/device/device.actions';
import { selectAutomationAlias, selectAutomationAliasEdited } from "src/app/store/device/device.selector";

@Component({
	selector: 'app-automations-alias-dialog',
	templateUrl: './automations-alias-dialog.component.html',
})

export class AutomationsAliasDialogComponent implements OnInit, OnDestroy {
	automationsAlias$: Observable<AutomationsAlias | undefined>;
	automationsAliasSub: Subscription | undefined;
	automationsAliasEdited$: Observable<boolean | undefined>;
	automationsAliasEditedSub: Subscription | undefined;

	aliasForm: FormGroup = null;

	typeAlias: string;

	automationsAlias: AutomationsAlias;
	listAlias: any[];

	screenWidth: number;

	TYPE_ALIAS_INPUT = "TYPE_ALIAS_INPUT";
	TYPE_ALIAS_OUTPUT = "TYPE_ALIAS_OUTPUT";
	TYPE_ALIAS_SELECTOR = "TYPE_ALIAS_SELECTOR";
	constructor(
		private formBuilder: FormBuilder,
		private store: Store<AppState>,
		public ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
	) {
		if (config.data !== undefined && config.data.typeAlias !== undefined) {
			this.typeAlias = config.data.typeAlias;
		}
		this.initSelectors();
	}

	initSelectors(): void {
		this.automationsAlias$ = this.store.select(selectAutomationAlias);
		this.automationsAliasEdited$ = this.store.select(selectAutomationAliasEdited);
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceActions.getAutomationsAlias());
		this.automationsAliasSub = this.automationsAlias$.subscribe(automationsAlias => {
			this.automationsAlias = automationsAlias;
			this.buildForm();
		});
		this.automationsAliasEditedSub = this.automationsAliasEdited$.subscribe(succ => {
			if (succ) {
				this.store.dispatch(DeviceActions.getAutomationsAlias());
				this.ref.close();
			}
		});
		StandardNewValidator.setFirstCompile(true);
		this.onResize();
	}

	buildForm() {
		if (this.automationsAlias !== undefined && this.automationsAlias.Inputs !== undefined && this.automationsAlias.Inputs.length > 0 && this.typeAlias === this.TYPE_ALIAS_INPUT) {
			this.aliasForm = this.formBuilder.group(this.buildFormInput());
		}
		if (this.automationsAlias !== undefined && this.automationsAlias.Outputs !== undefined && this.automationsAlias.Outputs.length > 0 && this.typeAlias === this.TYPE_ALIAS_OUTPUT) {
			this.aliasForm = this.formBuilder.group(this.buildFormOutput());
		}
		if (this.automationsAlias !== undefined && this.automationsAlias.Selectors !== undefined && this.automationsAlias.Selectors.length > 0 && this.typeAlias === this.TYPE_ALIAS_SELECTOR) {
			this.aliasForm = this.formBuilder.group(this.buildFormSelector());
		}
	}

	buildFormInput() {
		const res = {};
		this.listAlias = [];
		if (this.automationsAlias !== undefined && this.automationsAlias.Inputs !== undefined && this.automationsAlias.Inputs.length > 0)
			this.automationsAlias.Inputs.forEach((input) => {
				this.listAlias.push(input);
				res[input.Id] = [this.translateData(input.Description), [Validators.required, Validators.pattern(COMMAND_NAME_VALID_REGEXP), Validators.pattern(COMMAND_NAME_VALID_REGEXP_NOT_DOT)]];
			});
		return res;
	}

	buildFormOutput() {
		const res = {};
		this.listAlias = [];
		if (this.automationsAlias !== undefined && this.automationsAlias.Outputs !== undefined && this.automationsAlias.Outputs.length > 0)
			this.automationsAlias.Outputs.forEach((output) => {
				this.listAlias.push(output);
				res[output.Id] = [this.translateData(output.Description), [Validators.required, Validators.pattern(COMMAND_NAME_VALID_REGEXP), Validators.pattern(COMMAND_NAME_VALID_REGEXP_NOT_DOT)]];
			});
		return res;
	}

	buildFormSelector() {
		const res = {};
		this.listAlias = [];
		if (this.automationsAlias !== undefined && this.automationsAlias.Selectors !== undefined && this.automationsAlias.Selectors.length > 0)
			this.automationsAlias.Selectors.forEach((selector) => {
				this.listAlias.push(selector);
				res[selector.Id] = [this.translateData(selector.Description), [Validators.required, Validators.pattern(COMMAND_NAME_VALID_REGEXP), Validators.pattern(COMMAND_NAME_VALID_REGEXP_NOT_DOT)]];
			});
		return res;
	}

	restoreDefault(alias: any) {
		this.aliasForm.controls[alias.Id].setValue(this.translateData(alias.DefaultDescription));
	}

	ngOnDestroy(): void {
		this.automationsAliasSub?.unsubscribe();
		this.automationsAliasEditedSub?.unsubscribe();
	}

	get f(): any {
		return this.aliasForm.controls;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.aliasForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.aliasForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const automationsAlias = new AutomationsAlias();
		if (this.typeAlias === this.TYPE_ALIAS_INPUT) {
			automationsAlias.Inputs = [];
			for (const inp of this.listAlias) {
				const input: InputsAlias = {
					...inp, Description: this.aliasForm.controls[inp.Id].value,
					DefaultDescription: undefined, DefaultStateOn: undefined, DefaultStateOff: undefined
				};
				if (input.Description === this.translateData(inp.DefaultDescription)) {
					input.Description = inp.DefaultDescription;
				}
				automationsAlias.Inputs.push(input);
			}
		}
		if (this.typeAlias === this.TYPE_ALIAS_OUTPUT) {
			automationsAlias.Outputs = [];
			for (const out of this.listAlias) {
				const output: OutputsAlias = {
					...out, Description: this.aliasForm.controls[out.Id].value,
					DefaultDescription: undefined, DefaultStateOn: undefined, DefaultStateOff: undefined
				};
				if (output.Description === this.translateData(out.DefaultDescription)) {
					output.Description = out.DefaultDescription;
				}
				automationsAlias.Outputs.push(output);
			}
		}
		if (this.typeAlias === this.TYPE_ALIAS_SELECTOR) {
			automationsAlias.Selectors = [];
			for (const sel of this.listAlias) {
				const selector: SelectorsAlias = {
					...sel, Description: this.aliasForm.controls[sel.Id].value,
					DefaultDescription: undefined
				};
				if (selector.Description === this.translateData(sel.DefaultDescription)) {
					selector.Description = sel.DefaultDescription;
				}
				automationsAlias.Selectors.push(selector);
			}
		}
		this.store.dispatch(DeviceActions.editAutomationsAlias({ automationsAlias }));
	}

	closeDialog(): void {
		this.ref.close();
	}

	translateData(str: string): string {
		let resStr = '';
		const fields = str.split('{');
		for (const field of fields) {
			if (field.indexOf('}') >= 0) {
				const toTranslate = field.split('}');
				resStr = resStr + this.translate.instant(toTranslate[0]) + toTranslate[1];
			} else {
				resStr = resStr + field;
			}
		}
		return resStr;
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	newShowErrors(type: AutomationsAliasDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: AutomationsAliasDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}
}